import React from 'react';
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute'; // This will now have the hierarchical logic

// Corrected Imports for Public Pages to match file names precisely
import HomePage from '../pages/public/Home';
import AboutPage from '../pages/public/AboutUs';
import LoginPage from '../pages/public/Login';
import SignupPage from '../pages/public/RegisterPage';
import CoursesPage from '../pages/public/Courses';
import ForumPage from '../pages/public/Forum';
import DemoClassPage from '../pages/public/DemoClass';
import ContactPage from '../pages/public/ContactPage';
import AuthCallback from '../pages/AuthCallback';
import AuthRejected from '../pages/public/AuthRejected';
import DemoConfirmation from '../pages/public/DemoConfirmation';
import Blogs from '../pages/public/Blogs';
import BlogDetails from '../pages/public/BlogDetails';
import CourseDetails from '../pages/public/CourseDetails'; // ✅ Import CourseDetails
import PlacementsPage from '../pages/public/PlacementsPage';



// Import Dashboard Layouts
import PublicLayout from '../components/layout/PublicLayout';
import DashboardLayout from '../components/layout/DashboardLayout';

// Student Dashboard Components
import StudentDashboard from '../pages/dashboard/student/StudentDashboard';
import MyCourses from '../pages/dashboard/student/components/MyCourses';
import CourseModulesPage from '../pages/dashboard/student/components/CourseModulesPage';

import ForumDiscussion from '../pages/dashboard/student/components/ForumDiscussion';
import StudentProfile from '../pages/dashboard/student/components/Profile';


// Mentor Dashboard Components
import MentorDashboard from '../pages/dashboard/mentor/MentorDashboard';
import Approvals from '../pages/dashboard/mentor/components/Approvals';
import StudentsList from '../pages/dashboard/mentor/components/StudentsList';
import CourseUpload from '../pages/dashboard/mentor/components/CourseUpload';
import MentorProfile from '../pages/dashboard/mentor/components/MentorProfile';
import CreateBlog from '../pages/dashboard/mentor/components/CreateBlog';
import ManageCourses from '../pages/dashboard/mentor/components/ManageCourses';
import ManageMentors from '../pages/dashboard/mentor/components/ManageMentors'; // Corrected import path
import ManageCourseEnrollments from '../pages/dashboard/mentor/components/ManageCourseEnrollments';
import ManageBatches from '../pages/dashboard/mentor/components/ManageBatches';
import BatchDetailPage from '../pages/dashboard/mentor/components/BatchDetailPage'; // ✅ Import BatchDetailPage
import CourseDetail from '../pages/dashboard/mentor/components/CourseDetail'; // ✅ Import CourseDetail
import ManageBlogs from '../pages/dashboard/mentor/components/ManageBlogs';
import ManageCompanies from '../pages/dashboard/mentor/components/ManageCompanies';

// ✅ Import ManageBatches
// Admin Dashboard Page
import AdminDashboard from '../pages/dashboard/mentor/components/AdminDashboard';
import ViewDemoBookings from '../pages/dashboard/mentor/components/ViewDemoBookings';
import ManagePages from '../pages/dashboard/mentor/components/ManagePages';


const UnauthorizedPage = () => <div>Unauthorized Access</div>;


const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* 🌐 Public Routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/courses" element={<CoursesPage />} />
        <Route path="/courses/slug/:slug" element={<CourseDetails />} />
        <Route path="/courses/:id" element={<CourseDetails />} /> {/* ✅ This is the dynamic route for single course */}
        <Route path="/forum" element={<ForumPage />} />
        <Route path="/book-demo" element={<DemoClassPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/blogs/:slug" element={<BlogDetails />} />
        <Route path="/auth/rejected" element={<AuthRejected />} />
        <Route path="/demo-confirmation" element={<DemoConfirmation />} />
        <Route path="/placements" element={<PlacementsPage />} />
      </Route>

      {/* 🚀 Top-level Redirect Routes */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* 👨‍💼 Admin (Full-access mentor) Protected Route */}
      <Route element={<ProtectedRoute requireAdmin={true} />}> {/* This means only ADMIN users can access children */}
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route path="admin" element={<AdminDashboard />} />
          <Route path="admin/manage-pages" element={<ManagePages />} />
          <Route path="/dashboard/admin/manage-blogs" element={<ManageBlogs />} />
          <Route path="admin/create-blog" element={<CreateBlog />} />
          <Route path="admin/manage-courses" element={<ManageCourses />} />
          <Route path="/dashboard/admin/courses/:courseId" element={<CourseDetail />} />
          <Route path="admin/create-blog/:id?" element={<CreateBlog />} />
          <Route path="admin/course-detail/:courseId" element={<CourseDetail />} />
          <Route path="admin/manage-courses/upload/:id?" element={<CourseUpload />} />
          <Route path="admin/mentors" element={<ManageMentors />} />
          <Route path="admin/students" element={<StudentsList />} />
          <Route path="admin/profile" element={<MentorProfile />} />
          <Route path="admin/manage-enrollments/:courseId?" element={<ManageCourseEnrollments />} />
          <Route path="admin/student-profile/:id" element={<StudentProfile />} />
          <Route path="admin/demo-bookings" element={<ViewDemoBookings />} />
          <Route path="admin/manage-batches" element={<ManageBatches />} /> {/* ✅ Admin can manage batches */}
          <Route path="admin/batches/:batchId" element={<BatchDetailPage />} />
          <Route path="admin/manage-companies" element={<ManageCompanies />} />
          {/* ✅ Admin can view course details */}
        </Route>
      </Route>

      {/* 👨‍🏫 Mentor Protected Routes */}
      <Route element={<ProtectedRoute requiredRole="mentor" />}> {/* This means only MENTOR OR ADMIN users can access children */}
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route path="mentor" element={<MentorDashboard />} />
          <Route path="mentor/approvals" element={<Approvals />} />
          <Route path="mentor/students" element={<StudentsList />} />

          <Route path="mentor/manage-courses" element={<ManageCourses />} />
          <Route path="mentor/manage-courses/upload/:id?" element={<CourseUpload />} />
          <Route path="mentor/manage-courses/:courseId" element={<CourseDetail />} />
          <Route path="mentor/courses/:courseId" element={<CourseDetail />} />

          <Route path="mentor/profile" element={<MentorProfile />} />
          <Route path="mentor/manage-enrollments/:courseId?" element={<ManageCourseEnrollments />} />
          <Route path="mentor/student-profile/:id" element={<StudentProfile />} />
          <Route path="mentor/manage-batches" element={<ManageBatches />} />
          <Route path="mentor/batches/:batchId" element={<BatchDetailPage />} />

        </Route>
      </Route>

      {/* 🎓 Student Protected Routes */}
      <Route element={<ProtectedRoute requiredRole="student" />}> {/* This means only STUDENT OR ADMIN/MENTOR (if they share routes) */}
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route path="student" element={<StudentDashboard />} />
          <Route path="student/my-courses" element={<MyCourses />} />
              <Route path="student/my-courses/:courseId" element={<CourseModulesPage />} />

          <Route path="student/forum" element={<ForumDiscussion />} />
          <Route path="student/profile" element={<StudentProfile />} />
        </Route>
      </Route>

      {/* Fallback route for 404 Not Found */}
      <Route path="*" element={<div>404 Page Not Found</div>} />
    </Routes>
  );
};

export default AppRoutes;